import React, { useEffect, useState, useCallback } from "react";

/**
 * GDPR-compliant cookie consent banner.
 *
 * Compliance against Garante Privacy guidelines (Provv. 231/2021) + EU GDPR:
 *  - 3 symmetric buttons (Accept all / Reject all / Customize) with equal weight
 *  - No X close button (forces an explicit choice)
 *  - Granular consent per category (analytics, marketing, preferences)
 *  - Technical cookies always on (no consent needed)
 *  - Choice persisted in localStorage; banner reopens automatically when called
 *    via window event `openCookiePreferences` (used by the footer link)
 *  - Helper window.hasCookieConsent(category) for other modules to gate scripts
 */

const STORAGE_KEY = "cookieConsent_v2";

const CATEGORIES = [
  {
    key: "necessary",
    label: "Cookie tecnici (necessari)",
    description:
      "Indispensabili per il funzionamento del sito: login, carrello, sessione, preferenze lingua. Non richiedono consenso secondo la normativa.",
    locked: true,
    defaultValue: true,
  },
  {
    key: "analytics",
    label: "Cookie di analisi",
    description:
      "Ci aiutano a capire come usi il sito (pagine viste, percorsi di acquisto) in forma anonima/aggregata, così possiamo migliorarlo.",
    locked: false,
    defaultValue: false,
  },
  {
    key: "marketing",
    label: "Cookie di marketing",
    description:
      "Usati per mostrarti annunci personalizzati su altri siti e misurare l'efficacia delle nostre campagne (es. Meta/Facebook, Google Ads).",
    locked: false,
    defaultValue: false,
  },
  {
    key: "preferences",
    label: "Cookie di preferenze",
    description:
      "Ricordano scelte come tema, lingua, articoli salvati, per migliorare la tua esperienza nelle visite successive.",
    locked: false,
    defaultValue: false,
  },
];

const defaultPrefs = () =>
  Object.fromEntries(CATEGORIES.map((c) => [c.key, c.defaultValue]));

const acceptAllPrefs = () =>
  Object.fromEntries(CATEGORIES.map((c) => [c.key, true]));

const rejectAllPrefs = () =>
  Object.fromEntries(CATEGORIES.map((c) => [c.key, c.locked ? true : false]));

const loadPrefs = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

const savePrefs = (prefs) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      ...prefs,
      timestamp: new Date().toISOString(),
      version: 2,
    }));
  } catch { /* ignore */ }
};

// Global helper used by other modules (analytics scripts, pixel loaders, ...)
// to gate themselves. Returns true for "necessary" even before any choice.
if (typeof window !== "undefined") {
  window.hasCookieConsent = (category) => {
    if (category === "necessary") return true;
    const p = loadPrefs();
    return !!(p && p[category]);
  };
}

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  // Show banner on first visit; allow reopening via custom event
  useEffect(() => {
    const existing = loadPrefs();
    if (!existing) {
      setTimeout(() => setShowBanner(true), 700);
    }
    const reopenHandler = () => {
      const current = loadPrefs() || defaultPrefs();
      setPrefs({ ...defaultPrefs(), ...current });
      setShowCustomize(true);
      setShowBanner(true);
    };
    window.addEventListener("openCookiePreferences", reopenHandler);
    return () => window.removeEventListener("openCookiePreferences", reopenHandler);
  }, []);

  const finalise = useCallback((finalPrefs) => {
    savePrefs(finalPrefs);
    setShowBanner(false);
    setShowCustomize(false);
    // Notify any listener that may need to (re)init analytics with the new
    // consent state.
    try { window.dispatchEvent(new CustomEvent("cookieConsentChanged", { detail: finalPrefs })); } catch { /* ignore */ }
  }, []);

  const handleAcceptAll = () => finalise(acceptAllPrefs());
  const handleRejectAll = () => finalise(rejectAllPrefs());
  const handleCustomize = () => {
    setPrefs(loadPrefs() || defaultPrefs());
    setShowCustomize(true);
  };
  const handleSaveCustom = () => finalise(prefs);

  const toggleCategory = (key) => {
    const cat = CATEGORIES.find((c) => c.key === key);
    if (cat?.locked) return;
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
  };

  if (!showBanner) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[10000] bg-gray-900 text-white shadow-2xl border-t border-gray-700"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      data-testid="cookie-banner"
    >
      <div className="max-w-5xl mx-auto p-4 md:p-6">
        {!showCustomize && (
          <>
            <h2 id="cookie-banner-title" className="text-lg md:text-xl font-semibold mb-2">
              🍪 La tua privacy è importante
            </h2>
            <p id="cookie-banner-description" className="text-sm text-gray-200 leading-relaxed mb-4">
              Usiamo cookie tecnici necessari per il funzionamento del sito. Con il tuo consenso,
              utilizziamo anche cookie di analisi, marketing e preferenze per migliorare la tua
              esperienza. Puoi accettare, rifiutare o personalizzare le tue scelte. Per saperne
              di più leggi la nostra{" "}
              <a href="/privacy-policy" className="underline hover:text-amber-300">
                Privacy Policy
              </a>.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <button
                type="button"
                onClick={handleRejectAll}
                className="px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium text-sm border border-gray-500"
                data-testid="cookie-reject-all"
              >
                Rifiuta tutto
              </button>
              <button
                type="button"
                onClick={handleCustomize}
                className="px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium text-sm border border-gray-500"
                data-testid="cookie-customize"
              >
                Personalizza
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white font-medium text-sm border border-gray-500"
                data-testid="cookie-accept-all"
              >
                Accetta tutto
              </button>
            </div>
          </>
        )}

        {showCustomize && (
          <>
            <h2 className="text-lg md:text-xl font-semibold mb-2">
              ⚙️ Personalizza le preferenze
            </h2>
            <p className="text-sm text-gray-300 mb-4">
              Scegli quali categorie di cookie autorizzare. I cookie tecnici non possono essere
              disattivati perché necessari al funzionamento.
            </p>
            <div className="space-y-3 max-h-[55vh] overflow-y-auto pr-2">
              {CATEGORIES.map((c) => (
                <div
                  key={c.key}
                  className="bg-gray-800 rounded-lg p-3 border border-gray-700"
                  data-testid={`cookie-cat-${c.key}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white">{c.label}</p>
                      <p className="text-xs text-gray-400 mt-1">{c.description}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer shrink-0">
                      <input
                        type="checkbox"
                        checked={!!prefs[c.key]}
                        disabled={c.locked}
                        onChange={() => toggleCategory(c.key)}
                        className="sr-only peer"
                        data-testid={`cookie-toggle-${c.key}`}
                        aria-label={`Attiva/disattiva ${c.label}`}
                      />
                      <div className={`w-11 h-6 rounded-full transition-colors ${
                        c.locked ? "bg-green-700" : prefs[c.key] ? "bg-amber-500" : "bg-gray-600"
                      } peer-focus:ring-2 peer-focus:ring-amber-400`}>
                        <div className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                          prefs[c.key] ? "translate-x-5" : "translate-x-0.5"
                        }`} />
                      </div>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
              <button
                type="button"
                onClick={handleRejectAll}
                className="px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm border border-gray-500"
                data-testid="cookie-customize-reject-all"
              >
                Rifiuta tutto
              </button>
              <button
                type="button"
                onClick={handleSaveCustom}
                className="px-4 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-semibold text-sm"
                data-testid="cookie-save-custom"
              >
                Salva preferenze
              </button>
              <button
                type="button"
                onClick={handleAcceptAll}
                className="px-4 py-2.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-sm border border-gray-500"
                data-testid="cookie-customize-accept-all"
              >
                Accetta tutto
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export { CookieBanner };
export default CookieBanner;
